import { markLifecycleTiming } from "../../core/lifecycle-timings.ts";
import { getOwnerTaskStore } from "../../core/tasks/owner-store.js";
import { yieldToEventLoop } from "../../utils/event-loop.ts";
import {
	interactiveEngineNeedsExplicitTermination,
	interruptBlockedInteractiveEngine,
	terminateInteractiveEngine,
} from "../interactive-engine/extension-ui-bridge.ts";
import { IsolatedInteractiveRuntime } from "../interactive-engine/isolated-runtime.js";
import {
	dismissRemoteProxy,
	remoteEngineProxyOwner,
	remoteProxyHandlesCtrlC,
} from "../interactive-engine/remote-input-ownership.ts";
import { isRpcRequestAcceptedFailure } from "../rpc/rpc-transport-error.ts";
import { showTaskInspector } from "../rpc/task-ui-bridge.js";
import { StartupIdentityComponent } from "./components/startup-identity.ts";
import { COMPACTION_ALREADY_IN_PROGRESS_WARNING } from "./interactive-bash-compact.ts";
import { routeGlobalClearInput } from "./interactive-global-clear.ts";
import { isPhysicalCtrlC, isPhysicalEscape, isSafetyKeyRelease } from "./interactive-key-identity.ts";
import { InteractiveModeBase } from "./interactive-mode-base.ts";
import { pasteClipboardImageToEditor, recordTimeSinceReset } from "./interactive-mode-deps.ts";
import { pauseAndAbortInteractiveSession } from "./interactive-pause.ts";
import { restoreFailedSubmissionDraft, restoreRefusedSubmission } from "./interactive-prompt-restore.ts";

export function registerStartupInputListeners(mode: InteractiveModeBase): void {
	mode.addTuiInputListener(() =>
		mode.builtInHeader instanceof StartupIdentityComponent ? void mode.builtInHeader.settle() : undefined,
	);
	mode.addTuiInputListener((data) =>
		routeGlobalClearInput(data, {
			// Physical identity first: safety routing must survive an `app.clear` remap.
			matchesCtrlC: isPhysicalCtrlC,
			matchesEscape: isPhysicalEscape,
			isSafetyKeyRelease,
			matchesClear: (candidate) => mode.keybindings.matches(candidate, "app.clear"),
			hasOverlay: () => mode.ui.hasOverlay(),
			blockingInlineCustomUiActive: () => mode.blockingInlineCustomUiDepth > 0,
			editorOwnsInput: () => mode.editorContainer.children.includes(mode.editor),
			remoteEngineProxyOwner: () =>
				remoteEngineProxyOwner(mode.runtimeHost, {
					hasOverlay: () => mode.ui.hasOverlay(),
					inlineComponents: () => mode.editorContainer.children,
				}),
			remoteProxyHandlesCtrlC: (owner) => remoteProxyHandlesCtrlC(mode.runtimeHost, owner),
			onRemoteProxyDismiss: (owner) => {
				dismissRemoteProxy(mode.runtimeHost, owner);
			},
			engineNeedsExplicitTermination: () => interactiveEngineNeedsExplicitTermination(mode.runtimeHost),
			onEngineTerminate: () => {
				terminateInteractiveEngine(mode.runtimeHost);
			},
			onClear: () => mode.handleCtrlC(),
			requestRender: () => mode.ui.requestRender(),
		}),
	);
}

InteractiveModeBase.prototype.setupKeyHandlers = async function (this: InteractiveModeBase): Promise<void> {
	registerStartupInputListeners(this);
	// Set up handlers on defaultEditor - they use this.editor for text access
	// so they work correctly regardless of which editor is active
	this.defaultEditor.onEscape = () => {
		if (!this.session.isStreaming && interruptBlockedInteractiveEngine(this.runtimeHost)) return;
		if (this.session.isStreaming || this.session.agent.hasQueuedMessages() || this.session.queuedMessagesPaused) {
			pauseAndAbortInteractiveSession(this, { restoreQueuedMessages: true });
		} else if (this.session.isBashRunning) {
			this.session.abortBash();
		} else if (this.isBashMode) {
			this.editor.setText("");
			this.isBashMode = false;
			this.updateEditorBorderColor();
		} else if (!this.editor.getText().trim()) {
			// Double-escape with empty editor triggers /tree, /fork, or nothing based on setting
			const action = this.settingsManager.getDoubleEscapeAction();
			if (action !== "none") {
				const now = Date.now();
				if (now - this.lastEscapeTime < 500) {
					if (action === "tree") {
						this.showTreeSelector();
					} else {
						this.showUserMessageSelector();
					}
					this.lastEscapeTime = 0;
				} else {
					this.lastEscapeTime = now;
				}
			}
		}
	};

	// Register app action handlers
	this.defaultEditor.onAction("app.clear", () => this.handleCtrlC());
	this.defaultEditor.onCtrlD = () => this.handleCtrlD();
	this.defaultEditor.onAction("app.suspend", () => this.handleCtrlZ());
	this.defaultEditor.onAction("app.thinking.cycle", () => {
		void this.cycleThinkingLevel().catch((error: Error) => this.showError(error.message));
	});
	this.defaultEditor.onAction("app.model.cycleForward", () => {
		void (async () => {
			await this.ensureDeferredStartupComplete();
			await this.cycleModel("forward");
		})();
	});
	this.defaultEditor.onAction("app.model.cycleBackward", () => {
		void (async () => {
			await this.ensureDeferredStartupComplete();
			await this.cycleModel("backward");
		})();
	});

	// Global debug handler on TUI (works regardless of focus)
	this.ui.onDebug = () => this.handleDebugCommand();
	this.defaultEditor.onAction("app.model.select", () => {
		void (async () => {
			await this.ensureDeferredStartupComplete();
			this.showModelSelector();
		})();
	});
	this.defaultEditor.onAction("app.tools.expand", () => this.toggleToolOutputExpansion());
	this.defaultEditor.onAction("app.thinking.toggle", () => this.toggleThinkingBlockVisibility());
	this.defaultEditor.onAction("app.editor.external", () => {
		void this.openExternalEditor();
	});
	this.defaultEditor.onAction("app.message.followUp", () => this.handleFollowUp());
	this.defaultEditor.onAction("app.message.dequeue", () => this.handleDequeue());
	this.defaultEditor.onAction("app.session.new", () => this.handleClearCommand());
	this.defaultEditor.onAction("app.session.tree", () => this.showTreeSelector());
	this.defaultEditor.onAction("app.session.fork", () => this.showUserMessageSelector());
	this.defaultEditor.onAction("app.session.resume", () => this.showSessionSelector());

	this.defaultEditor.onChange = (text: string) => {
		const wasBashMode = this.isBashMode;
		this.isBashMode = text.trimStart().startsWith("!");
		if (wasBashMode !== this.isBashMode) {
			this.updateEditorBorderColor();
		}
	};

	// Handle clipboard paste (triggered on Ctrl+V). Copied files use their original paths,
	// images are attached via temporary files, and plain text is the final fallback.
	this.defaultEditor.onPasteImage = () => {
		this.handleClipboardImagePaste();
	};
};

InteractiveModeBase.prototype.handleClipboardImagePaste = async function (this: InteractiveModeBase): Promise<void> {
	await pasteClipboardImageToEditor(this.editor, () => this.ui.requestRender(), {
		showWarning: (message) => this.showWarning(message),
		isBashMode: this.isBashMode,
	});
};

InteractiveModeBase.prototype.deliverStartupReplayPrompt = function (this: InteractiveModeBase, text: string): void {
	if (this.onInputCallback) {
		if (!text.startsWith("/")) {
			this.renderDeferredUserInput(text);
		}
		const callback = this.onInputCallback;
		this.onInputCallback = undefined;
		callback({ text, draft: text });
	} else {
		this.pendingUserInputs.push({ text, draft: text });
	}
};

InteractiveModeBase.prototype.drainStartupReplayCommands = async function (this: InteractiveModeBase): Promise<void> {
	while (this.pendingUserInputs.length === 0 && this.startupReplayActiveInput) {
		const activeInput = this.startupReplayActiveInput;
		await this.defaultEditor.onSubmit?.(activeInput);
		if (this.editor.getText().trim() === activeInput.trim()) {
			this.editor.setText("");
		}
		if (this.startupReplayActiveInput?.trim() === activeInput.trim()) break;
	}
};

InteractiveModeBase.prototype.advanceStartupInputReplay = function (
	this: InteractiveModeBase,
	submittedText: string,
): void {
	if (this.startupReplayActiveInput?.trim() !== submittedText.trim()) return;
	this.startupReplayActiveInput = undefined;

	while (this.startupReplayInputs.length > 0) {
		const nextInput = this.startupReplayInputs.shift();
		if (nextInput === undefined) break;
		const trimmed = nextInput.trimStart();
		if (trimmed.startsWith("/") || trimmed.startsWith("!")) {
			this.startupReplayActiveInput = nextInput.trim();
			this.editor.setText(this.startupReplayActiveInput);
			this.ui.requestRender();
			return;
		}
		this.deliverStartupReplayPrompt(nextInput);
	}

	if (this.startupDraftText !== undefined) {
		this.editor.setText(this.startupDraftText);
		this.startupDraftText = undefined;
		this.ui.requestRender();
	}
};

InteractiveModeBase.prototype.setupEditorSubmitHandler = function (this: InteractiveModeBase): void {
	this.defaultEditor.onSubmit = async (text: string) => {
		if (!this.firstSubmitRecorded) {
			this.firstSubmitRecorded = true;
			recordTimeSinceReset("interactive-first-submit");
			markLifecycleTiming("interactive-first-submit");
		}
		// pi-tui trims, expands pastes, and clears the editor before it calls
		// onSubmit, so the callback argument is already reduced. Prefer the buffer
		// CustomEditor snapshotted for this very dispatch, which is what the user
		// actually had, and fall back to the argument for other editors and for
		// tests that invoke onSubmit directly.
		const submittedDraft = this.defaultEditor.takeSubmittedDraft?.() ?? text;
		text = text.trim();
		if (!text) return;

		if (this.startupReplayActiveInput && this.startupReplayActiveInput.trim() !== text.trim()) {
			this.startupReplayInputs.push(text);
			this.editor.addToHistory?.(text);
			this.editor.setText("");
			return;
		}
		try {
			// Handle commands
			if (text === "/tasks" || text.startsWith("/tasks ")) {
				if (this.runtimeHost instanceof IsolatedInteractiveRuntime) {
					await this.runtimeHost.openTaskInspector(text.slice(6).trim() || undefined);
					return;
				}
				if (!getOwnerTaskStore(this.session)) this.session.getAgentTaskHost();
				const store = getOwnerTaskStore(this.session);
				this.editor.setText("");
				if (!store) {
					this.showStatus("Launched agents and shells will appear here.");
					return;
				}
				await showTaskInspector(
					{ custom: (factory, options) => this.showExtensionCustom(factory, options) },
					store,
					text.slice(6).trim() || undefined,
				);
				return;
			}
			if (text === "/settings") {
				this.showSettingsSelector();
				this.editor.setText("");
				return;
			}
			if (text === "/scoped-models") {
				this.editor.setText("");
				await this.ensureDeferredStartupComplete();
				this.showModelsSelector();
				return;
			}
			if (text === "/model" || text.startsWith("/model ")) {
				const searchTerm = text.startsWith("/model ") ? text.slice(7).trim() : undefined;
				this.editor.setText("");
				await this.ensureDeferredStartupComplete();
				await this.handleModelCommand(searchTerm);
				return;
			}
			if (text === "/thinking" || text.startsWith("/thinking ")) {
				this.editor.setText("");
				await this.ensureDeferredStartupComplete();
				await this.handleThinkingCommand(text.startsWith("/thinking ") ? text.slice(10).trim() : undefined);
				return;
			}
			if (text === "/export" || text.startsWith("/export ")) {
				await this.handleExportCommand(text);
				this.editor.setText("");
				return;
			}
			if (text === "/import" || text.startsWith("/import ")) {
				await this.handleImportCommand(text);
				this.editor.setText("");
				return;
			}
			if (text === "/share") {
				await this.handleShareCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/copy") {
				await this.handleCopyCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/name" || text.startsWith("/name ")) {
				await this.handleNameCommand(text);
				this.editor.setText("");
				return;
			}
			if (text === "/session") {
				this.handleSessionCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/tools") {
				this.editor.setText("");
				await this.handleToolsCommand();
				return;
			}
			if (text === "/changelog") {
				this.handleChangelogCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/hotkeys") {
				this.handleHotkeysCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/fork") {
				this.showUserMessageSelector();
				this.editor.setText("");
				return;
			}
			if (text === "/clone") {
				this.editor.setText("");
				await this.handleCloneCommand();
				return;
			}
			if (text === "/tree") {
				this.showTreeSelector();
				this.editor.setText("");
				return;
			}
			if (text === "/trust") {
				this.showTrustSelector();
				this.editor.setText("");
				return;
			}
			if (/^\/login(?:\s|$)/.test(text)) {
				this.editor.setText("");
				await this.handleLoginCommand(text.slice("/login".length).trim() || undefined);
				return;
			}
			if (/^\/logout(?:\s|$)/.test(text)) {
				this.editor.setText("");
				await this.showOAuthSelector("logout", text.slice("/logout".length).trim() || undefined);
				return;
			}
			if (text === "/new") {
				this.editor.setText("");
				await this.handleClearCommand();
				return;
			}
			if (/^\/compact(?:\s|$)/.test(text)) {
				this.editor.setText("");
				if (text !== "/compact") {
					this.showWarning("Usage: /compact");
					return;
				}
				// A manual request can take over automatic compaction, but a second manual
				// compaction or branch summary still has no safe independent owner.
				const automaticCompaction =
					this.session.compactionReason === "threshold" || this.session.compactionReason === "overflow";
				if (this.manualCompactionTakeoverPending || (this.compactionActive && !automaticCompaction)) {
					this.showWarning(COMPACTION_ALREADY_IN_PROGRESS_WARNING);
					return;
				}
				await this.handleCompactCommand();
				return;
			}
			if (text === "/reload") {
				this.editor.setText("");
				await this.ensureDeferredStartupComplete();
				await this.handleReloadCommand();
				return;
			}
			if (text === "/debug") {
				this.handleDebugCommand();
				this.editor.setText("");
				return;
			}
			if (text === "/arminsayshi") {
				this.handleArminSaysHi();
				this.editor.setText("");
				return;
			}
			if (text === "/dementedelves") {
				this.handleDementedDelves();
				this.editor.setText("");
				return;
			}
			if (text === "/resume") {
				this.showSessionSelector();
				this.editor.setText("");
				return;
			}
			if (text === "/quit" || text === "/exit") {
				this.editor.setText("");
				await this.shutdown();
				return;
			}

			if (text.startsWith("/") && this.deferredStartupPending) {
				this.editor.addToHistory?.(text);
				this.editor.setText("");
				this.ui.requestRender();
				await yieldToEventLoop();
				await this.ensureDeferredStartupComplete();
				await this.session.prompt(text);
				return;
			}
			// Handle bash command (! for normal, !! for excluded from context)
			if (text.startsWith("!")) {
				const isExcluded = text.startsWith("!!");
				const command = isExcluded ? text.slice(2).trim() : text.slice(1).trim();
				if (command) {
					if (this.session.isBashRunning) {
						this.showWarning("A bash command is already running. esc cancel first.");
						this.editor.setText(text);
						return;
					}
					this.editor.addToHistory?.(text);
					await this.handleBashCommand(command, isExcluded);
					this.isBashMode = false;
					this.updateEditorBorderColor();
					return;
				}
			}

			// Queue input behind compaction-queued messages (extension commands execute immediately)
			if (
				this.compactionActive ||
				(!this.session.queuedMessagesPaused &&
					(this.compactionFlushPending || this.compactionSendFailed || this.promptTurn?.dispatched === false))
			) {
				if (this.isExtensionCommand(text)) {
					this.editor.addToHistory?.(text);
					this.editor.setText("");
					await this.session.prompt(text);
				} else {
					this.queueCompactionMessage(text, "steer");
				}
				return;
			}

			// A paused submission releases the session queue before its own turn, so move held
			// messages there first to keep them ahead of this one.
			if (this.session.queuedMessagesPaused && this.compactionSendFailed && !this.compactionFlushPending) {
				await this.flushCompactionQueue({ willRetry: false });
			}

			// If streaming, use prompt() with steer behavior
			// This handles extension commands (execute immediately), prompt template expansion, and queueing
			if ((this.session.isStreaming || this.promptTurn !== undefined) && !this.session.queuedMessagesPaused) {
				this.editor.addToHistory?.(text);
				this.editor.setText("");
				let accepted = false;
				try {
					await this.session.prompt(text, {
						streamingBehavior: "steer",
						preflightResult: (success, disposition) => {
							accepted = success;
							if (success && disposition === "started" && !text.startsWith("/"))
								this.renderDeferredUserInput(text);
						},
					});
				} catch (error) {
					if (accepted) throw error;
					if (isRpcRequestAcceptedFailure(error)) {
						this.showError(error.message);
						return;
					}
					restoreRefusedSubmission(this, error, submittedDraft);
					return;
				}
				this.updatePendingMessagesDisplay();
				this.ui.requestRender();
				return;
			}

			// Normal message submission
			// First, move any pending bash components to chat
			this.flushPendingBashComponents();
			// The draft travels with this submission, never through shared state: a
			// later submission whose trimmed text is identical must not be able to
			// claim, or clear, this one's exact buffer.
			const submission = { text, draft: submittedDraft };

			if (this.onInputCallback) {
				if (!text.startsWith("/")) {
					this.renderDeferredUserInput(text);
				}
				this.promptTurn ??= { dispatched: false };
				this.onInputCallback(submission);
			} else {
				this.pendingUserInputs.push(submission);
			}
			this.editor.addToHistory?.(text);
		} catch (error) {
			// Direct dispatch branches above hand text straight to the engine and
			// never reach runUserPromptTurn(), so this is the only place their send
			// failures can put the draft back. Anything else is not ours to absorb.
			if (!restoreFailedSubmissionDraft(this, error, submittedDraft)) throw error;
		} finally {
			this.advanceStartupInputReplay?.(text);
		}
	};
};
